import { useIntl } from 'react-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SortingRule } from 'react-table'
import { useHistory } from 'react-router-dom'

import routes from 'app/routes'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { allLogsFetchedSelector, auditLogsSelector } from './selectors'
import AuditTable from './AuditTable'

import { AuditBody, AuditContainer, AuditSpinnerContainer } from './styles'
import AuditHeader from './AuditHeader'
import { MobileSortBy } from 'components/Table'
import { AUDIT_HEADERS } from './consts'
import { auditTableMessages } from './messages'
import { getSortOptions } from 'components/Table/utils'
import type { DomainLogs } from './types'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import usePermissions from 'features/auth/permissions/usePermissions'
import { DOMAIN_ADMIN_SOMEWHERE } from 'features/auth/permissions/consts'
import { auditLogsActions, fetchAuditLogsThunk } from 'features/audit/slice'
import { commonMessages } from 'app/i18n/messages'
import ShowMoreButton from 'components/Button/ShowMoreButton'
import { Spinner } from 'components/Loader'

function Audit() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const history = useHistory()

  const rootDomainId = useAppSelector(currentRootDomainIdSelector)

  const [hasDomainAdminPermissionSomewhere] = usePermissions({
    allow: [DOMAIN_ADMIN_SOMEWHERE],
    domainId: rootDomainId || undefined,
  })

  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)

  const auditLogs = useAppSelector(auditLogsSelector)
  const allLogsFetched = useAppSelector(allLogsFetchedSelector)

  const fetchAuditLogs = useCallback(
    async (currentPage: number) => {
      try {
        setIsLoading(true)
        await dispatch(fetchAuditLogsThunk(currentPage))
      } catch (error) {
        setShowError(true)
        console.log('Error fetching audit logs', error)
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (hasDomainAdminPermissionSomewhere) {
      fetchAuditLogs(currentPage)
    } else {
      history.push(routes.homesJobs(rootDomainId))
    }
  }, [
    dispatch,
    history,
    rootDomainId,
    fetchAuditLogs,
    currentPage,
    hasDomainAdminPermissionSomewhere,
  ])

  const handleRenewLogs = useCallback(async () => {
    await dispatch(auditLogsActions.clear())
    if (currentPage === 0) {
      await fetchAuditLogs(currentPage)
    } else {
      setCurrentPage(0)
    }
  }, [dispatch, fetchAuditLogs, currentPage])

  const headers = useMemo(
    () => ({
      date: {
        label: formatMessage(auditTableMessages.date),
        id: AUDIT_HEADERS.DATE,
        desc: true,
      },
      domain: {
        label: formatMessage(auditTableMessages.domain),
        id: AUDIT_HEADERS.DOMAIN,
      },
      user: {
        label: formatMessage(auditTableMessages.user),
        id: AUDIT_HEADERS.USER,
      },
      action: {
        label: formatMessage(auditTableMessages.action),
        id: AUDIT_HEADERS.ACTION,
      },
    }),
    [formatMessage]
  )

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])

  const [sortBy, setSortBy] = useState<SortingRule<DomainLogs>>(sortOptions[0])

  const handleOnShowMoreClick = () => {
    setCurrentPage(currentPage + 1)
  }

  return (
    <AuditContainer>
      <AuditBody>
        <AuditHeader handleRenewLogs={handleRenewLogs} />
        {showError && <>{formatMessage(commonMessages.commonError)}</>}
        <MobileSortBy<DomainLogs>
          headers={headers}
          sortOptions={sortOptions}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
        <AuditTable
          data={auditLogs}
          headers={headers}
          defaultSortBy={sortOptions[0]}
          sortBy={sortBy}
          setSortBy={setSortBy}
          rootDomainId={rootDomainId}
        />
        {isLoading && (
          <AuditSpinnerContainer>
            <Spinner size={1} />
          </AuditSpinnerContainer>
        )}
        {!allLogsFetched && (
          <ShowMoreButton
            onClick={handleOnShowMoreClick}
            disabled={isLoading}
          />
        )}
      </AuditBody>
    </AuditContainer>
  )
}

export default Audit
